"use client";

import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../AuthContext";
import { PAYPAL_CONFIG, validatePayPalConfig } from "../config/paypalConfig";
import { createWellnessPayPalOrder, captureWellnessPayPalOrder } from "../services/paypalService";
import { useCart } from "../context/CartContext";
import "../css/ReservationForm.css";
import "../css/Cart.css";

const parsePrice = (v) => {
  const n = parseFloat(String(v ?? 0).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export default function WellnessReservationForm() {
  const { currentUser, authReady } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({
    guestName1: "",
    guestName2: "",
    age1: "",
    age2: "",
    date: "",
    email: "",
    phone: "",
    wellnessType: "",
    note: "",
  });

  const { addItem, items: cartItems } = useCart();

  const [step, setStep] = useState("form"); // "form" | "payment"
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [cartAdded, setCartAdded] = useState(false);
  const paymentStarted = useRef(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const snap = await getDocs(collection(db, "wellnessServices"));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => a.id.localeCompare(b.id));
        setServices(data);
      } catch (error) {
        console.error("Error fetching wellness services:", error);
      }
    };
    fetchServices();
  }, []);

  // Pre-fill form when navigated from Cart's "Book This Item" button
  useEffect(() => {
    const cartItem = location.state?.cartItem;
    if (!cartItem || services.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      guestName1: cartItem.primaryGuest || prev.guestName1,
      guestName2: cartItem.guestName2 || prev.guestName2,
      date: cartItem.date || prev.date,
      email: cartItem.email || prev.email,
      phone: cartItem.phone || prev.phone,
      wellnessType: cartItem.itemId || prev.wellnessType,
      note: cartItem.note || prev.note,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.cartItem, services.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedService = services.find((s) => s.id === formData.wellnessType);

  const resetForm = () => {
    paymentStarted.current = false;
    setFormData({
      guestName1: "",
      guestName2: "",
      age1: "",
      age2: "",
      date: "",
      email: "",
      phone: "",
      wellnessType: "",
      note: "",
    });
    setStep("form");
  };

  const handleAddToCart = () => {
    setSubmitMessage(null);
    if (!selectedService) {
      setSubmitMessage({ type: "error", text: "Please select a wellness experience." });
      return;
    }
    if (!formData.guestName1.trim()) {
      setSubmitMessage({ type: "error", text: "Please enter the primary guest name." });
      return;
    }
    if (!formData.date) {
      setSubmitMessage({ type: "error", text: "Please select a date." });
      return;
    }
    if (!formData.email.trim()) {
      setSubmitMessage({ type: "error", text: "Please enter your email address." });
      return;
    }

    const cartItemId = `wellness-${selectedService.id}-${formData.date}`;

    if (cartItems.some((i) => i.cartItemId === cartItemId)) {
      setSubmitMessage({ type: "info", text: "This experience for that date is already in your cart." });
      return;
    }

    addItem({
      cartItemId,
      category: "wellness",
      itemId: selectedService.id,
      itemName: selectedService.name,
      price: selectedService.price || "",
      priceNumeric: parsePrice(selectedService.price),
      checkIn: null,
      checkOut: null,
      nights: null,
      date: formData.date,
      guests: null,
      primaryGuest: formData.guestName1,
      guestName2: formData.guestName2,
      email: formData.email,
      phone: formData.phone,
      note: formData.note,
      addedAt: Date.now(),
    });

    setCartAdded(true);
    setSubmitMessage({ type: "success", text: "Added to your cart!", link: { label: "View Cart →", to: "/cart" } });
    setTimeout(() => { setCartAdded(false); setSubmitMessage(null); }, 5000);
  };

  const handleContinueToPayment = (e) => {
    e.preventDefault();
    setSubmitMessage(null);

    if (!selectedService) {
      setSubmitMessage({ type: "error", text: "Please select a wellness experience." });
      return;
    }

    setStep("payment");
  };

  if (!authReady) {
    return null;
  }

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        paddingBottom: "120px",
      }}
    >
      <video
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          objectFit: "cover",
          zIndex: -2,
        }}
      >
        <source src="/videos/sunsetbluedark.mp4" type="video/mp4" />
      </video>

      <div
        className="reservation-form-container wellness"
        style={{ marginBottom: "60px" }}
      >
        <h2>Wellness Reservation</h2>

        {!currentUser ? (
          <p style={{ textAlign: "center", color: "white", position: "relative", zIndex: 2 }}>
            You'll need an account to book a wellness experience.{" "}
            <Link to="/sign-in" style={{ color: "white", textDecoration: "underline", fontWeight: 700 }}>
              Sign in
            </Link>{" "}
            or{" "}
            <Link to="/sign-up" style={{ color: "white", textDecoration: "underline", fontWeight: 700 }}>
              create an account
            </Link>{" "}
            to continue.
          </p>
        ) : (
          <>
            {step === "form" && (
              <form className="reservation-form" onSubmit={handleContinueToPayment}>
                {/* 1. Guest names */}
                <div className="guest-names-row">
                  <div className="form-group">
                    <label htmlFor="guestName1">Primary Guest Name</label>
                    <input
                      id="guestName1"
                      name="guestName1"
                      type="text"
                      placeholder="Enter primary guest name"
                      value={formData.guestName1}
                      onChange={handleChange}
                      required
                      autoComplete="off"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="guestName2">Companion Guest Name </label>
                    <input
                      id="guestName2"
                      name="guestName2"
                      type="text"
                      placeholder="Enter companion guest name"
                      value={formData.guestName2}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* 2. Ages */}
                <div className="age-row">
                  <div className="form-group">
                    <label htmlFor="age1">Age – Primary Guest</label>
                    <input
                      id="age1"
                      name="age1"
                      type="number"
                      placeholder="Enter age"
                      value={formData.age1}
                      onChange={handleChange}
                      min={1}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="age2">Age – Companion Guest</label>
                    <input
                      id="age2"
                      name="age2"
                      type="number"
                      placeholder="Enter age"
                      value={formData.age2}
                      onChange={handleChange}
                      min={1}
                    />
                  </div>
                </div>

                {/* 3. Date */}
                <label htmlFor="date">Date</label>
                <input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />

                {/* 4. Email */}
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />

                {/* 5. Phone */}
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />

                {/* 6. Wellness Type */}
                <label htmlFor="wellnessType">Select Wellness Experience</label>
                <select
                  id="wellnessType"
                  name="wellnessType"
                  value={formData.wellnessType}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select experience...
                  </option>
                  {services.filter((s) => s.available !== false).map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name} — {service.price}
                    </option>
                  ))}
                </select>

                {/* 7. Special Requests */}
                <label htmlFor="note">Special Requests</label>
                <textarea
                  id="note"
                  name="note"
                  placeholder="Add any special requests (optional)"
                  value={formData.note}
                  onChange={handleChange}
                  rows={3}
                />

                <button type="submit">Continue to Payment</button>
                <button
                  type="button"
                  className={`btn-add-to-cart${cartAdded ? " added" : ""}`}
                  onClick={handleAddToCart}
                >
                  {cartAdded ? "Added to Cart ✓" : "Add to Cart"}
                </button>
              </form>
            )}

            {step === "payment" && selectedService && (
              <div className="reservation-form" style={{ position: "relative", zIndex: 2 }}>
                <div
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 8,
                  }}
                >
                  <p style={{ margin: 0, fontWeight: 700 }}>{selectedService.name}</p>
                  <p style={{ margin: 0 }}>{formData.date}</p>
                  <p style={{ margin: 0, opacity: 0.85, fontSize: "0.9rem" }}>
                    Final total is calculated securely at checkout.
                  </p>
                </div>

                {!validatePayPalConfig() ? (
                  <p style={{ textAlign: "center", color: "#ffa1a1" }}>
                    PayPal isn't configured yet. Add REACT_APP_PAYPAL_CLIENT_ID to your .env file.
                  </p>
                ) : (
                  <PayPalScriptProvider options={{ clientId: PAYPAL_CONFIG.clientId, currency: "USD" }}>
                    <PayPalButtons
                      style={{ layout: "vertical" }}
                      disabled={isSubmitting}
                      createOrder={async () => {
                        paymentStarted.current = true;
                        try {
                          const order = await createWellnessPayPalOrder({ serviceId: selectedService.id });
                          return order.orderId;
                        } catch (error) {
                          console.error("createOrder error:", error);
                          setSubmitMessage({
                            type: "error",
                            text: error.message || "Could not start checkout. Please try again.",
                          });
                          throw error;
                        }
                      }}
                      onApprove={async (data) => {
                        setIsSubmitting(true);
                        setSubmitMessage(null);
                        try {
                          const result = await captureWellnessPayPalOrder(data.orderID, {
                            ...formData,
                            serviceId: selectedService.id,
                            serviceName: selectedService.name,
                          });
                          navigate("/booking-confirmed", {
                            state: {
                              pointsAwarded: result.pointsAwarded,
                              totalCharged: result.amountPaid,
                              bookingCount: 1,
                              newBalance: result.newBalance ?? null,
                            },
                          });
                        } catch (error) {
                          console.error("capture error:", error);
                          setSubmitMessage({
                            type: "error",
                            text: error.message || "Payment could not be completed. Please try again.",
                          });
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                      onCancel={() => {
                        if (paymentStarted.current) {
                          setSubmitMessage({ type: "info", text: "Payment canceled." });
                          paymentStarted.current = false;
                        }
                      }}
                      onError={(error) => {
                        console.error("PayPal error:", error);
                        setSubmitMessage({
                          type: "error",
                          text: "Something went wrong with PayPal. Please try again.",
                        });
                      }}
                    />
                  </PayPalScriptProvider>
                )}

                <button type="button" onClick={() => { paymentStarted.current = false; setStep("form"); }} disabled={isSubmitting}>
                  Back to Details
                </button>
              </div>
            )}
          </>
        )}

        {submitMessage && (
          <div
            style={{
              marginTop: 12,
              position: "relative",
              zIndex: 2,
              color:
                submitMessage.type === "success"
                  ? "#3eea94"
                  : submitMessage.type === "info"
                  ? "#ffd27a"
                  : "#ffa1a1",
              textAlign: "center",
              fontWeight: 600,
              textShadow: "0 1px 10px rgba(0,0,0,0.15)",
            }}
          >
            {submitMessage.text}
            {submitMessage.link && (
              <Link
                to={submitMessage.link.to}
                style={{ marginLeft: 8, color: "#d4a853", textDecoration: "underline" }}
              >
                {submitMessage.link.label}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
